import React, { useEffect, useRef } from 'react';
import { Card, Badge } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface Property {
  id: string;
  address: string;
  latitude?: number;
  longitude?: number;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  status: string;
  isSubject?: boolean;
}

interface PropertyMapProps {
  subjectProperty: {
    address: string;
    latitude?: number;
    longitude?: number;
    prediction_value: number;
  };
  comparables: Property[];
  title?: string;
}

// Custom marker icons
const createCustomIcon = (color: string, isSubject: boolean = false) => {
  return new L.DivIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        background-color: ${color};
        width: ${isSubject ? '30px' : '20px'};
        height: ${isSubject ? '30px' : '20px'};
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${isSubject ? '14px' : '10px'};
      ">
        ${isSubject ? '★' : '●'}
      </div>
    `,
    iconSize: [isSubject ? 30 : 20, isSubject ? 30 : 20],
    iconAnchor: [isSubject ? 15 : 10, isSubject ? 15 : 10],
  });
};

const subjectIcon = createCustomIcon('#e74c3c', true); // Red for subject
const comparableIcon = createCustomIcon('#3498db', false); // Blue for comparables

// Component to fit bounds when properties change
const FitBounds: React.FC<{ properties: Array<{ latitude?: number; longitude?: number }> }> = ({ properties }) => {
  const map = useMap();
  
  useEffect(() => {
    const validProperties = properties.filter(p => p.latitude && p.longitude);
    if (validProperties.length > 0) {
      const bounds = L.latLngBounds(
        validProperties.map(p => [p.latitude!, p.longitude!])
      );
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, properties]);
  
  return null;
};

const PropertyMap: React.FC<PropertyMapProps> = ({
  subjectProperty,
  comparables,
  title = "Property Location & Comparables"
}) => {
  const mapRef = useRef<L.Map | null>(null);

  // Mock coordinates for demonstration (in production, you'd get these from geocoding)
  const getMockCoordinates = (address: string, index: number = 0) => {
    // Phoenix metro area coordinates with slight variations
    const baseLatitude = 33.4484;
    const baseLongitude = -112.0740;
    const offset = 0.1; // ~11km radius
    
    return {
      latitude: baseLatitude + (Math.random() - 0.5) * offset,
      longitude: baseLongitude + (Math.random() - 0.5) * offset
    };
  };

  // Prepare subject property with coordinates
  const subjectWithCoords = {
    ...subjectProperty,
    ...(subjectProperty.latitude && subjectProperty.longitude 
      ? {} 
      : getMockCoordinates(subjectProperty.address, 0)
    )
  };

  // Prepare comparables with coordinates
  const comparablesWithCoords = comparables.map((comp, index) => ({
    ...comp,
    ...(comp.latitude && comp.longitude 
      ? {} 
      : getMockCoordinates(comp.address, index + 1)
    )
  }));

  const allProperties = [
    { ...subjectWithCoords, id: 'subject', isSubject: true },
    ...comparablesWithCoords
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Default center (Phoenix, AZ)
  const defaultCenter: [number, number] = [33.4484, -112.0740];

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{title}</h4>
          <div className="d-flex gap-2">
            <Badge bg="danger" className="d-flex align-items-center gap-1">
              <span style={{ fontSize: '12px' }}>★</span> Subject Property
            </Badge>
            <Badge bg="primary" className="d-flex align-items-center gap-1">
              <span style={{ fontSize: '8px' }}>●</span> Comparables
            </Badge>
          </div>
        </div>
        
        <div style={{ height: '400px', width: '100%' }}>
          <MapContainer
            center={defaultCenter}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <FitBounds properties={allProperties} />
            
            {/* Subject Property Marker */}
            {subjectWithCoords.latitude && subjectWithCoords.longitude && (
              <Marker
                position={[subjectWithCoords.latitude, subjectWithCoords.longitude]}
                icon={subjectIcon}
              >
                <Popup>
                  <div className="text-center">
                    <h6 className="mb-2 text-danger">Subject Property</h6>
                    <p className="mb-1 small">{subjectProperty.address}</p>
                    <h5 className="text-success mb-0">
                      {formatCurrency(subjectProperty.prediction_value)}
                    </h5>
                    <small className="text-muted">AI Predicted Value</small>
                  </div>
                </Popup>
              </Marker>
            )}
            
            {/* Comparable Properties Markers */}
            {comparablesWithCoords.map((comp) => (
              comp.latitude && comp.longitude && (
                <Marker
                  key={comp.id}
                  position={[comp.latitude, comp.longitude]}
                  icon={comparableIcon}
                >
                  <Popup>
                    <div className="text-center" style={{ minWidth: '200px' }}>
                      <h6 className="mb-2 text-primary">Comparable Sale</h6>
                      <p className="mb-1 small">{comp.address}</p>
                      <h5 className="text-primary mb-2">{formatCurrency(comp.price)}</h5>
                      <div className="small text-muted mb-1">
                        {comp.bedrooms}bd • {comp.bathrooms}ba • {formatNumber(comp.square_feet)}sf
                      </div>
                      <Badge 
                        bg={comp.status === 'sold' ? 'success' : 'warning'} 
                        className="small"
                      >
                        {comp.status}
                      </Badge>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        </div>
        
        <div className="mt-3 text-center">
          <small className="text-muted">
            Interactive map showing subject property and {comparables.length} comparable sales
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default PropertyMap; 